import { MAP_SEVERITY_COLORS } from '@protector/entities/choropleth/constants';
import { getChoroplethSeverityColor } from '@protector/entities/choropleth/utils';
import type { Monitoring as AreaDayMonitoring, MonitoringExtraDimension } from '@protector/entities/monitoring/models';
import { BreakdownType } from '@protector/entities/property/constants';
import type { DiagnosticBreakdown, Property } from '@protector/entities/property/models';
import type { CurrentInfo, Region } from '@protector/entities/region/models';

import type { CustomFormTimelineRecord, TimelineCustomFormRecord } from '@protector/entities/timeline/models';

import { normalizeSeverityLevel } from '@protector/entities/timeline/utils';
import { LissThresholds, Threshold } from '@protector/utils/constants';
import { validateNumberOrZero } from '@protector/utils/number';
import type { Dictionary, IndexedName, Page, UUID } from '@protector/utils/types';
import type { NotificationInstance } from 'antd/es/notification/interface';
import i18n from 'config/i18n';
import { DEFAULT_UUID } from 'core/shared/map/map.functions';
import type { ISTMarkerProps, TMarkerPinStatus } from 'core/shared/map/map.models';
import { sortArrayByDateAscending } from 'core/utils/functions';
import { captureError } from 'infra/monitoring/monitoring.utils';
import _, { isEmpty } from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';
import timezoneMoment from 'moment-timezone';
import type {
  Annotation,
  ArrayOfPages,
  GetNotesMarkerIconProps,
  IExtendAttrSpain,
  Monitoring,
  MonitoringPoint,
  MonitoringSorted,
  Phenology,
  TimelineWindow,
  TimelineWindowEvents,
  TimelineWindowEventsFromBff
} from 'pages/timeline/timeline.models';
import { EventKeys, EventType, FILE_TYPE } from 'pages/timeline/timeline.models';
import { map } from 'rxjs/operators';
import type { IEditAreaRequestParams } from './area-day-info/components/area-day-info.resume.models';
import type { MonitoringDetailThreshold } from './monitoring.models';
import { LastEventType } from './region-select/timeline.region-select.models';
import { EMPTY_THRESHOLD } from './timeline.constants';
import { saveEditAreaInfo } from './timeline.service';

export const formatPages = (events: TimelineWindow[], pageSize: number): ArrayOfPages<TimelineWindow> => {
  const pages: Page<TimelineWindow>[] = [];
  let elementsLeft = events.length;
  let controlArray: TimelineWindow[] = events;
  for (let i = 0; i < Math.ceil(events.length / pageSize); i++) {
    pages[i] = {
      number: i,
      size: elementsLeft - pageSize >= 0 ? pageSize : elementsLeft,
      mutableContent:
        controlArray.length - pageSize >= 0
          ? sortArrayByDateAscending(controlArray.slice(0, pageSize), 'start_date')
          : sortArrayByDateAscending(controlArray.slice(0, elementsLeft), 'start_date'),
      pages: Math.ceil(events.length / pageSize),
      total: events.length
    };
    if (controlArray.length - pageSize >= 0) {
      controlArray = controlArray.slice(pageSize, controlArray.length);
    }
    elementsLeft -= pageSize;
  }
  return pages;
};

export const getLastAreaEvent = (currentInfo?: CurrentInfo): LastEventType | null => {
  const monitoring = currentInfo?.last_monitoring;
  const spray = currentInfo?.last_spray;

  if (!monitoring && !spray) return null;

  if (!monitoring) return LastEventType.SPRAY;
  if (!spray) return LastEventType.MONITORING;

  const isMonitoringAfterSprayStartDate = moment(monitoring.date).diff(moment(spray.start_date)) > 0;
  return isMonitoringAfterSprayStartDate ? LastEventType.MONITORING : LastEventType.SPRAY;
};

export const getThresholdColor = (area: Region) => {
  const result = { backgroundColor: '#707374' };

  if (!area.current_info) {
    return result;
  }

  if (area.current_info.crop_ended) {
    result.backgroundColor = '#C1C5C8';

    return result;
  }

  const lastEvent = getLastAreaEvent(area.current_info);

  if (!lastEvent) {
    return result;
  }

  if (lastEvent === LastEventType.MONITORING && validateNumberOrZero(area.current_info.severity_level)) {
    const severityLevel = normalizeSeverityLevel(area.current_info.severity_level);
    if (severityLevel < Number(LissThresholds.MIN_CONTROL)) {
      result.backgroundColor = '#2FB966';
    } else if (severityLevel < Number(LissThresholds.MIN_DAMAGE)) {
      result.backgroundColor = '#FFCA28';
    } else {
      result.backgroundColor = '#F85555';
    }
  }

  if (lastEvent === LastEventType.SPRAY) {
    result.backgroundColor = '#0568FA';
  }

  return result;
};

export const parseTooltipSeeds = (seeds: string[]) => {
  return seeds.reduce((total, current, idx, array) => (idx > 1 ? total + current + (idx < array.length - 1 ? ', ' : '') : ''), '');
};

export const getAreaThreshold = (area: Region): Threshold => {
  let threshold = Threshold.NONE;
  if (!area.current_info) {
    return threshold;
  }
  const lastEvent = getLastAreaEvent(area.current_info);
  if (area.current_info.crop_ended) {
    threshold = Threshold.ENDED;
  } else if (validateNumberOrZero(area.current_info.severity_level) && lastEvent === LastEventType.MONITORING) {
    const severityLevel = normalizeSeverityLevel(area.current_info.severity_level);
    if (severityLevel < (LissThresholds.MIN_CONTROL as number)) {
      threshold = Threshold.ACCEPTANCE;
    } else if (severityLevel < (LissThresholds.MIN_DAMAGE as number)) {
      threshold = Threshold.CONTROL;
    } else {
      threshold = Threshold.DAMAGE;
    }
  } else if (area.current_info && lastEvent === LastEventType.SPRAY) {
    threshold = Threshold.SPRAY;
  }
  return threshold;
};

const hasSprayWithoutLastVisit = (currentInfo?: CurrentInfo): boolean => Boolean(currentInfo?.last_spray && !currentInfo.last_visit);

const hasSprayOnLastVisitDay = (currentInfo?: CurrentInfo): boolean => {
  if (!currentInfo || currentInfo.crop_ended) return false;

  const sprayDate = currentInfo.last_spray?.start_date?.slice(0, 10);
  const lastVisitDate = currentInfo.last_visit?.slice(0, 10);
  if (!sprayDate || !lastVisitDate) return false;

  return sprayDate === lastVisitDate;
};

export const buildDiagnosticBreakdown = (regions: Region[]): DiagnosticBreakdown => {
  const initialDiagnostic: DiagnosticBreakdown = {
    acceptance: 0,
    control: 0,
    damage: 0,
    undetermined: 0,
    spray: 0,
    breakdown_type: BreakdownType.AREA,
    valid_on: moment().toISOString()
  };

  (regions ?? []).forEach(region => {
    if (hasSprayOnLastVisitDay(region.current_info)) {
      initialDiagnostic.spray = (initialDiagnostic.spray ?? 0) + 1;
      return;
    }

    if (hasSprayWithoutLastVisit(region.current_info)) {
      initialDiagnostic.undetermined++;
      return;
    }

    const color = getChoroplethSeverityColor(region, region.current_info?.crop_ended ?? false);

    if (color === MAP_SEVERITY_COLORS[0]) initialDiagnostic.acceptance++;
    else if (color === MAP_SEVERITY_COLORS[1]) initialDiagnostic.control++;
    else if (color === MAP_SEVERITY_COLORS[2]) initialDiagnostic.damage++;
    else initialDiagnostic.undetermined++;
  });

  return initialDiagnostic;
};

export const getWindowEventTypes = (window: Record<string, TimelineWindowEvents>): string[] => {
  const eventKeys: Record<string, string> = EventKeys;
  return Object.keys(window).reduce<string[]>((acc, key) => {
    const isKnownEventKey = Object.hasOwn(EventKeys, key);
    if (isKnownEventKey && window[key]) {
      acc.push(eventKeys[key]);
    }
    return acc;
  }, []);
};

export const extractExtraDimensions = (extraDimensions: MonitoringExtraDimension[]) => {
  return extraDimensions.reduce((resources, extraDimension) => {
    return { ...resources, ...extraDimension.extra_dimensions };
  }, {});
};
const extractMarkerProps = (
  point: MonitoringPoint,
  type: EventType,
  propertyId: UUID,
  propertyTimezone: string,
  reverse = false,
  monitoringById: Dictionary<AreaDayMonitoring> = {}
) => {
  const mutableStatus: TMarkerPinStatus[] = [];

  if (point.forced_point) mutableStatus.push('simulated');

  if (point.image || point?.medias?.length) mutableStatus.push('have-photo');

  let position;

  if (reverse) {
    position = point.forced_point
      ? [point.forced_point.coordinates[1], point.forced_point.coordinates[0]]
      : [point.point.coordinates[1], point.point.coordinates[0]];
  } else {
    position = point.forced_point ? point.forced_point.coordinates : point.point.coordinates;
  }
  const monitoring = monitoringById[point.id];
  const resources = monitoring ? extractExtraDimensions(monitoring.inspections_extra_dimensions) : undefined;
  return {
    id: point.id,
    markerProps: {
      type,
      status: mutableStatus,
      position
    },
    popupProps: {
      ...point,
      popup: { ...point, image: point.image || point?.medias?.[0]?.url, propertyTimezone },
      propertyId,
      resources
    }
  };
};

export const getMonitoringMarkers = (
  monitoring: Monitoring | MonitoringSorted,
  propertyId: UUID,
  monitoringById: Dictionary<AreaDayMonitoring>,
  propertyTimezone: string,
  reverse = false
): ISTMarkerProps[] => {
  return monitoring.points.map(point =>
    extractMarkerProps(point, EventType.MONITORING, propertyId, propertyTimezone, reverse, monitoringById)
  ) as unknown as ISTMarkerProps[];
};

export const getPhenologyMarkers = (
  phenology: Phenology,
  propertyId: UUID,
  propertyTimezone: string,
  reverse = false
): ISTMarkerProps[] => {
  return phenology.samples.map(point =>
    extractMarkerProps(point as unknown as MonitoringPoint, EventType.PHENOLOGY, propertyId, propertyTimezone, reverse)
  ) as unknown as ISTMarkerProps[];
};

export const getAnnotationMarkers = (
  annotation: Annotation,
  propertyId: UUID,
  propertyTimezone: string,
  reverse = false
): ISTMarkerProps[] => {
  return annotation.annotations.map(point =>
    extractMarkerProps(point as unknown as MonitoringPoint, EventType.ANNOTATION, propertyId, propertyTimezone, reverse)
  ) as unknown as ISTMarkerProps[];
};

const dayOf = (value: string) => value.slice(0, 10);

export const fillWindowCustomForms = (
  windowEvents: TimelineWindowEventsFromBff,
  records: TimelineCustomFormRecord[],
  areaId?: string | null
): TimelineWindowEventsFromBff => {
  const existingRecords = windowEvents.custom_forms_window?.records ?? [];
  if (!records.length) return windowEvents;

  const existingIds = new Set(existingRecords.map(record => record.id));
  const startDay = windowEvents.startDate ? dayOf(windowEvents.startDate) : undefined;
  const endDay = windowEvents.endDate ? dayOf(windowEvents.endDate) : undefined;
  const addedRecords = records.filter(record => {
    if (existingIds.has(record.id)) return false;
    if (record.field_id && record.field_id !== areaId) return false;
    const recordDay = dayOf(record.reference_date);
    if (startDay && recordDay < startDay) return false;
    if (endDay && recordDay > endDay) return false;
    return true;
  });

  if (!addedRecords.length) return windowEvents;

  return { ...windowEvents, custom_forms_window: { records: [...existingRecords, ...addedRecords] } };
};

export const mergeCustomFormWindows = (
  windows: TimelineWindow[],
  records: Array<Pick<CustomFormTimelineRecord, 'field_id' | 'reference_date'>>,
  areaId?: string | null,
  seasonRanges: { start_date: string; end_date: string }[] = []
): TimelineWindow[] => {
  const isInSeason = (referenceDate: string) => {
    if (!seasonRanges.length) return true;
    const day = dayOf(referenceDate);
    return seasonRanges.some(season => day >= dayOf(season.start_date) && day <= dayOf(season.end_date));
  };
  const relevantDays = records
    .filter(record => !record.field_id || record.field_id === areaId)
    .filter(record => isInSeason(record.reference_date))
    .map(record => dayOf(record.reference_date))
    .filter((day, index, days) => days.indexOf(day) === index);

  const missingDays = relevantDays.filter(day => !windows.some(window => day >= dayOf(window.start_date) && day <= dayOf(window.end_date)));
  if (!missingDays.length) return windows;

  const customFormWindows = missingDays.map<TimelineWindow>(day => ({
    start_date: day,
    end_date: day,
    events: [EventType.CUSTOM_FORM],
    phenological_stage: ''
  }));

  return [...windows, ...customFormWindows].sort((left, right) => left.start_date.localeCompare(right.start_date));
};

function getCustomFormMarker(
  record: TimelineCustomFormRecord,
  windowEvents: TimelineWindowEvents | TimelineWindowEventsFromBff,
  propertyId: string,
  propertyTimezone: string,
  companyId?: string
): ISTMarkerProps | undefined {
  const location = record.location;

  if (
    !location ||
    !Number.isFinite(location.lat) ||
    !Number.isFinite(location.lng) ||
    Math.abs(location.lat) > 90 ||
    Math.abs(location.lng) > 180 ||
    (record.field_id && windowEvents.areaId && record.field_id !== windowEvents.areaId)
  ) {
    return undefined;
  }

  return {
    id: record.id,
    parentAreaId: record.field_id ?? undefined,
    description: record.form_title,
    propertyTimezone,
    customForm: companyId
      ? {
          title: record.form_title,
          companyId,
          propertyId,
          formId: record.form_id,
          recordId: record.id,
          formVersion: record.form_version
        }
      : undefined,
    markerProps: {
      type: EventType.CUSTOM_FORM,
      status: [],
      position: [location.lng, location.lat]
    },
    popupProps: {
      date: record.reference_date,
      sampling_id: '',
      image: '',
      medias: []
    }
  };
}

export function getMarkersFromWindowEvents(
  windowEvents: TimelineWindowEvents | TimelineWindowEventsFromBff,
  propertyId: string,
  propertyTimezone: string,
  selectedEvent = EventType.NONE,
  areaDayMonitorings: AreaDayMonitoring[] = [],
  reverse = false,
  companyId?: string
) {
  const markers: ISTMarkerProps[] = [];

  const includeAllEvents = selectedEvent === EventType.NONE;

  if (includeAllEvents || selectedEvent === EventType.MONITORING) {
    if (windowEvents.monitoring_window) {
      const monitoringById = _.keyBy(areaDayMonitorings, 'id');

      markers.push(...getMonitoringMarkers(windowEvents.monitoring_window, propertyId, monitoringById, propertyTimezone, reverse));
    }
  }

  if (includeAllEvents || selectedEvent === EventType.PHENOLOGY) {
    if (windowEvents.phenology_window) {
      markers.push(...getPhenologyMarkers(windowEvents.phenology_window, propertyId, propertyTimezone, reverse));
    }
  }

  if (includeAllEvents || selectedEvent === EventType.ANNOTATION) {
    if (windowEvents.annotation_window) {
      markers.push(...getAnnotationMarkers(windowEvents.annotation_window, propertyId, propertyTimezone, reverse));
    }
  }

  if (selectedEvent === EventType.NONE || selectedEvent === EventType.CUSTOM_FORM) {
    const records = windowEvents.custom_forms_window?.records ?? [];

    for (const record of records) {
      const marker = getCustomFormMarker(record, windowEvents, propertyId, propertyTimezone, companyId);

      if (marker) {
        markers.push(marker);
      }
    }
  }

  return markers;
}

export const shouldUpdateAreaMarkers = (
  eventsAreFromSelectedArea: boolean,
  computedMarkersCount: number,
  currentMarkersCount: number
): boolean => eventsAreFromSelectedArea || computedMarkersCount > 0 || currentMarkersCount > 0;

const getRightNameTresholdIcon = (id: string, severityPoints?: Dictionary<Threshold>) => {
  if (severityPoints?.[id]) {
    if (severityPoints[id] === Threshold.ACCEPTANCE) return 'T1';
    if (severityPoints[id] === Threshold.CONTROL) return 'T2';
    if (severityPoints[id] === Threshold.DAMAGE) return 'T3';
  }
  return 'No';
};

const getMonitoringMarkerIcon = (
  marker: ISTMarkerProps,
  hasPhoto: boolean,
  activePinColor: boolean,
  severityPoints?: Dictionary<Threshold>,
  hasMultiplePhotos?: boolean
): string => {
  const isSampling =
    marker.popupProps?.sampling_id && marker.popupProps.sampling_id !== DEFAULT_UUID && marker.popupProps.sampling_id.length > 0;
  const hasSelectedSeverity = severityPoints?.[marker.id] !== undefined;
  const shouldGetThresholdColor = activePinColor && !isSampling && hasSelectedSeverity;
  const thresholdColor = shouldGetThresholdColor ? getRightNameTresholdIcon(marker.id, severityPoints) : '';

  if (hasMultiplePhotos) {
    return `monitoringImage${thresholdColor}${marker.markerProps.status.includes('simulated') ? 'Simulated' : ''}MultiplePhoto${
      isSampling ? 'Sampling' : ''
    }`;
  }

  return `monitoringImage${thresholdColor}${marker.markerProps.status.includes('simulated') ? 'Simulated' : ''}${hasPhoto ? 'Photo' : ''}${
    isSampling ? 'Sampling' : ''
  }`;
};

export const getNotesMarkerIcon = ({
  isSingleOrMultiplePhotos,
  enableMultiplePhotos,
  hasPhoto,
  hasAudio,
  marker
}: GetNotesMarkerIconProps): string => {
  const isSimulated = marker.markerProps.status.includes('simulated');

  if (enableMultiplePhotos) {
    let noteIcon = 'note';

    if (isSimulated) noteIcon += 'Simulated';
    if (isSingleOrMultiplePhotos) noteIcon += 'Photo';
    if (hasAudio) noteIcon += 'Audio';

    return noteIcon;
  }

  if (hasPhoto) {
    return isSimulated ? 'annotationImageSimulatedPhoto' : 'annotationImagePhoto';
  }

  return isSimulated ? 'annotationImageSimulated' : 'annotationImage';
};

interface HasMedia {
  hasAudio: boolean;
  hasImage: boolean;
}

const TRAP_SEVERITY_ICON_SUFFIX: Partial<Record<Threshold, string>> = {
  [Threshold.NONE]: 'None',
  [Threshold.DAMAGE]: 'Damage',
  [Threshold.CONTROL]: 'Control',
  [Threshold.ACCEPTANCE]: 'Acceptance'
};

const getTrapMarkerIcon = (marker: ISTMarkerProps, activePinColor: boolean, severityPoints?: Dictionary<Threshold>): string => {
  const severity = activePinColor ? severityPoints?.[marker.id] : undefined;
  const severitySuffix = severity === undefined ? '' : TRAP_SEVERITY_ICON_SUFFIX[severity] ?? 'None';
  const simulatedSuffix = marker.markerProps.status.includes('simulated') ? 'Simulated' : '';
  const photoSuffix = marker.markerProps.status.includes('have-photo') ? 'Photo' : '';
  return `trapPin${severitySuffix}${simulatedSuffix}${photoSuffix}`;
};

const getRainGaugeMarkerIcon = (marker: ISTMarkerProps) =>
  marker.markerProps.status.includes('simulated') ? 'pluviometerPinSimulated' : 'pluviometerPinSelected';

const getMarkerWithoutAndWithImages = (
  marker: ISTMarkerProps,
  popupHasNotImages: boolean,
  activePinColor: boolean,
  hasMultiplePhotos: boolean,
  enableMultiplePhotos: boolean,
  hasMedia: HasMedia,
  severityPoints?: Dictionary<Threshold>
) => {
  const { hasAudio, hasImage } = hasMedia;
  if (marker.markerProps.type === EventType.PLUVIOMETER) {
    return getRainGaugeMarkerIcon(marker);
  }
  if (marker.markerProps.type === EventType.CUSTOM_FORM) return 'customFormImage';
  if (marker.markerProps.type === EventType.TRAP) {
    return getTrapMarkerIcon(marker, activePinColor, severityPoints);
  } else if (marker.markerProps.type === EventType.MONITORING) {
    const parameterHasMultiple = popupHasNotImages ? undefined : hasMultiplePhotos;
    return getMonitoringMarkerIcon(marker, !popupHasNotImages, activePinColor, severityPoints, parameterHasMultiple);
  } else if (marker.markerProps.type === EventType.PHENOLOGY) {
    if (!popupHasNotImages) {
      return marker.markerProps.status.includes('simulated') ? 'phenologicalStateImageSimulatedPhoto' : 'phenologicalStateImagePhoto';
    }
    return marker.markerProps.status.includes('simulated') ? 'phenologicalStateImageSimulated' : 'phenologicalStateImage';
  } else if (marker.markerProps.type === EventType.ANNOTATION) {
    return getNotesMarkerIcon({
      isSingleOrMultiplePhotos: hasMultiplePhotos || hasImage,
      enableMultiplePhotos,
      hasPhoto: !popupHasNotImages,
      hasAudio,
      marker
    });
  }
};

export const getRightMarkerIcon = (
  marker: ISTMarkerProps,
  severityPoints?: Dictionary<Threshold>,
  activePinColor = false,
  enableMultiplePhotos = false
) => {
  if (!marker.popupProps && !marker) return undefined;

  const popupMediasLength = marker.popupProps?.medias?.length ?? 0;

  const ifAnnotationHasMedias = marker.markerProps.type !== EventType.ANNOTATION && popupMediasLength > 1;

  const ifHasPictures = marker.popupProps?.medias && marker.popupProps.medias.filter(media => media.type === FILE_TYPE.PICTURES).length > 1;

  const hasMultiplePhotos = ifAnnotationHasMedias || ifHasPictures;

  const hasAudio = marker.popupProps?.medias?.length && marker.popupProps.medias.some(media => media.type === FILE_TYPE.AUDIO);
  const hasImage = marker.popupProps?.image && !isEmpty(marker.popupProps.image);

  const popupHasNotImages = marker?.popupProps && (marker.popupProps.medias?.length === 0 || isEmpty(marker.popupProps.image));

  return getMarkerWithoutAndWithImages(
    marker,
    !!popupHasNotImages,
    activePinColor,
    !!hasMultiplePhotos,
    enableMultiplePhotos,
    { hasAudio: !!hasAudio, hasImage: !!hasImage },
    severityPoints
  );
};

export interface SaveAreaInfoParams {
  area: Region;
  UpdateRegion$: (item: Region) => void;
  setIsSavingEditInfo: (item: boolean) => void;
  handleCloseEdit: () => void;
  currentAreaName: string;
  currentPlantingDay: Moment | null;
  currentEmergenceDay: Moment | null;
  currentHarvestDay: Moment | null;
  propertyTimeZone?: string;
  selectedVarieties: IndexedName[];
  selectedSeasonFieldId: string;
  updateCurrentAreaInfo?: (area: Region) => void;
  notification: NotificationInstance;
}

export const getLocaleFormattedDate = (date: Moment | null, timeZone: string): string => {
  if (!date?.isValid()) return '';
  const localeDate = timeZone ? timezoneMoment.tz(date.format('YYYY-MM-DD'), timeZone) : date;
  return localeDate.format('YYYY-MM-DDTHH:mm:ssZZ');
};

export const saveAreaInfo = ({
  propertyTimeZone = '',
  selectedSeasonFieldId,
  updateCurrentAreaInfo,
  setIsSavingEditInfo,
  currentEmergenceDay,
  currentPlantingDay,
  currentHarvestDay,
  selectedVarieties,
  currentAreaName,
  handleCloseEdit,
  UpdateRegion$,
  area,
  notification
}: SaveAreaInfoParams): void => {
  const plantingDate = getLocaleFormattedDate(currentPlantingDay, propertyTimeZone);
  const emergencyDate = getLocaleFormattedDate(currentEmergenceDay, propertyTimeZone);
  const harvestingDate = getLocaleFormattedDate(currentHarvestDay, propertyTimeZone);

  const requestParams: IEditAreaRequestParams = {
    areaName: currentAreaName,
    plantingDate,
    emergencyDate,
    harvestingDate,
    varieties: selectedVarieties.map(v => v.id),
    id: selectedSeasonFieldId
  };

  saveEditAreaInfo(requestParams)
    .pipe(map(response => response.data))
    .subscribe(
      () => {
        setIsSavingEditInfo(false);
        handleCloseEdit();
        const updatedRegion: Region = {
          ...area,
          name: currentAreaName,
          current_info: {
            ...area.current_info,
            area_in_hectares: area.current_info?.area_in_hectares ?? 0,
            severity_level: area.current_info?.severity_level ?? null,
            seeds: selectedVarieties.map(v => v.name),
            planting_day: plantingDate || area.current_info?.planting_day,
            emergence_day: emergencyDate || area.current_info?.emergence_day
          }
        };
        UpdateRegion$(updatedRegion);
        if (updateCurrentAreaInfo) {
          updateCurrentAreaInfo(area);
        }
      },
      error => {
        notification.error({ title: i18n.t('alert.messages.error') });
        captureError(error, { errorType: 'AxiosError', domain: 'Timeline' });
      }
    );
};

export const onHandleCreateRegionObject = (
  area: Region,
  currentAreaName: string,
  selectedVarieties: IndexedName[],
  plantingDate: string | null,
  emergencyDate: string | null,
  harvestingDate: string
): Region | null => {
  if (area.current_info) {
    const updatedRegion: Region = {
      ...area,
      name: currentAreaName,
      current_info: {
        ...area.current_info,
        area_in_hectares: area.current_info.area_in_hectares,
        severity_level: area.current_info.severity_level,
        seeds: selectedVarieties.map(v => v.name),
        planting_day: plantingDate ?? undefined,
        emergence_day: emergencyDate ?? undefined,
        harvest_day: harvestingDate
      }
    };

    return updatedRegion;
  }
  return null;
};

export interface IOnHandleUpdateAreaInfoParams {
  area: Region;
  currentAreaName: string;
  currentPlantingDay: string | null;
  currentEmergenceDay: string | null;
  currentHarvestDay: string;
  selectedVarieties: IndexedName[];
  selectedSeasonAreaId: UUID;
}

export const onHandleUpdateAreaInfo = ({
  area,
  currentAreaName,
  currentPlantingDay,
  currentEmergenceDay,
  currentHarvestDay,
  selectedVarieties,
  selectedSeasonAreaId
}: IOnHandleUpdateAreaInfoParams): Promise<Region | null | Error> => {
  const requestParams: IEditAreaRequestParams = {
    areaName: currentAreaName,
    plantingDate: currentPlantingDay,
    emergencyDate: currentEmergenceDay,
    harvestingDate: currentHarvestDay,
    varieties: selectedVarieties.map(v => v.id),
    id: selectedSeasonAreaId
  };

  return saveEditAreaInfo(requestParams)
    .toPromise()
    .then(() => {
      return onHandleCreateRegionObject(
        area,
        currentAreaName,
        selectedVarieties,
        currentPlantingDay,
        currentEmergenceDay,
        currentHarvestDay
      );
    })
    .catch((e: Error) => {
      captureError(e, { errorType: 'AxiosError', domain: 'Timeline' });
      return e;
    });
};

const checkValueIsOnThreshold = (value: number, threshold: MonitoringDetailThreshold, isPositiveIndicator = false) => {
  const interval = [threshold.left, threshold.right].sort((a, b) => a - b);
  return isPositiveIndicator ? value <= interval[1] : value >= interval[0];
};

export const getIndicatorThreshold = (value: number, thresholds?: MonitoringDetailThreshold[]): MonitoringDetailThreshold => {
  if (!thresholds?.length) return EMPTY_THRESHOLD;
  // A single-threshold indicator has nothing to compare directions against -- it's the only bucket.
  if (thresholds.length === 1) return thresholds[0];
  const isPositive = thresholds[0].right > thresholds[1].right;
  const sortedThresholdsDesc = [...thresholds].sort((thrA, thrB) => (isPositive ? thrA.left - thrB.left : thrB.left - thrA.left));
  return (
    sortedThresholdsDesc.find(thr => checkValueIsOnThreshold(value, thr, isPositive)) ?? sortedThresholdsDesc.at(-1) ?? EMPTY_THRESHOLD
  );
};

export const createBreadcrumbRegion = (
  regions: Dictionary<Region>,
  selectedProperty: Property | undefined,
  selectedRegionOrAreaId: string | undefined | null
): string | undefined => {
  const rootRegionId = selectedProperty?.root_region_id;

  if (!rootRegionId || !selectedRegionOrAreaId) return undefined;

  const regionsNames: string[] = [];
  let currentRegionId = selectedRegionOrAreaId;

  while (currentRegionId) {
    const region = regions[currentRegionId];
    if (!region) break;
    regionsNames.push(region.name);
    currentRegionId = region.parent_id ?? '';
  }

  regionsNames.reverse();
  const breadcrumb = regionsNames.slice(1).join(' > ');
  return breadcrumb;
};

export const setCorrectPixelRatioToIconImages = (image: { id: string; url: string }): number => {
  const includesMonitoringOrNote = image.id.includes('monitoring') || image.id.includes('note');
  const isSampling = image.id.includes('Sampling');

  if (includesMonitoringOrNote && isSampling) {
    return 2.8;
  }
  return includesMonitoringOrNote ? 2 : 1;
};

export const getStartDateFromMutableContentOfPage = (content: TimelineWindow[], isFirst: boolean): Moment | undefined => {
  return content[isFirst ? 0 : content.length - 1]?.start_date;
};

export const getSigpac = (area?: Region | null) => {
  const extended_attributes = area?.json_extended_attributes as unknown as IExtendAttrSpain;
  const sigpac: string = extended_attributes?.sigpacCode ?? null;

  if (!sigpac) return '';

  const dgcCode: string = extended_attributes?.dgcCode ?? null;
  return dgcCode ? `${sigpac}.${dgcCode}` : `${sigpac}`;
};
